import React, { FC, useState, useEffect } from "react";
import { Form, Col, Row, Checkbox, Tooltip, Alert } from "antd";
import { FeeRiskProfileDiagram } from "./fee-risk-profile-diagram";
import {
  validateLowestFee,
  validateHighestFee,
  validateAll,
} from "@virtual-pricing-director/vpd-illustration";
import { PriceInput } from "../input-number";
import { InfoCircleOutlined } from "@ant-design/icons";

import styles from "./fee-risk-profile.css";

const {
  ["container"]: containerClassName,
  ["header"]: headerClassName,
  ["form-container"]: formContainerClassName,
  ["form-item"]: formItemClassName,
  ["amount-input"]: amountInputClassname,
  ["alert"]: alertClassName,
} = styles;

type InfoTooltipProps = {
  text: string;
  children: React.ReactNode;
};

export const InfoTooltip: FC<InfoTooltipProps> = ({ text, children }) => (
  <Tooltip title={text} placement="right">
    <InfoCircleOutlined style={{ marginRight: 5 }} />
    {children}
  </Tooltip>
);

type FeeRiskProfileProps = {
  currencySymbol: string;
  legalFees: number | { lower: number; upper: number };
  feeRiskLower: number | null;
  feeRiskUpper: number | null;
  fixedFeeAlternative: number | null;
  cappedFeeAlternative: number | null;
  onFeeRiskLowerChange: (fee: number) => void;
  onFeeRiskUpperChange: (fee: number) => void;
  onFixedFeeAlternativeChange: (fee: number) => void;
  onCappedFeeAlternativeChange: (fee: number | null) => void;
  showCappedFee: boolean;
  onShowCappedFeeChange: (showCappedFee: boolean) => void;
};

export const FeeRiskProfile: FC<FeeRiskProfileProps> = ({
  currencySymbol,
  legalFees,
  feeRiskLower,
  feeRiskUpper,
  fixedFeeAlternative,
  cappedFeeAlternative,
  onFeeRiskLowerChange,
  onFeeRiskUpperChange,
  onFixedFeeAlternativeChange,
  onCappedFeeAlternativeChange,
  showCappedFee,
  onShowCappedFeeChange,
}) => {
  if (
    typeof legalFees !== "object" ||
    !Object.prototype.hasOwnProperty.call(legalFees, "lower") ||
    !Object.prototype.hasOwnProperty.call(legalFees, "upper")
  ) {
    return null;
  }
  const lowestAnticipatedFee = legalFees.lower;
  const highestAnticipatedFee = legalFees.upper;
  const [lowestFee, setLowestFee] = useState<number | null>(feeRiskLower);
  const [highestFee, setHighestFee] = useState<number | null>(feeRiskUpper);
  const [fixedFee, setFixedFee] = useState<number | null>(fixedFeeAlternative);
  const [cappedFee, setCappedFee] = useState<number | null>(cappedFeeAlternative);

  const [stagedLowestFee, setStagedLowestFee] = useState<number | null>(feeRiskLower);
  const [stagedHighestFee, setStagedHighestFee] = useState<number | null>(feeRiskUpper);
  const [stagedFixedFee, setStagedFixedFee] = useState<number | null>(fixedFeeAlternative);
  const [stagedCappedFee, setStagedCappedFee] = useState<number | null>(cappedFeeAlternative);

  const [feeRiskValid, setFeeRiskValid] = useState<boolean>(false);

  const [showFixedFeeWarning, setShowFixedFeeWarning] = useState<boolean>(false);
  const [showCappedFeeWarning, setShowCappedFeeWarning] = useState<boolean>(false);

  const validateAllFields = () => {
    if (
      validateAll({
        lowestAnticipatedFee,
        highestAnticipatedFee,
        lowestFee,
        highestFee,
        fixedFee,
        cappedFee,
      })
    ) {
      setFeeRiskValid(true);
    } else {
      setFeeRiskValid(false);
    }
  };

  const handleOnBlurLowestFee = () => {
    if (stagedLowestFee !== null && lowestFee !== stagedLowestFee) {
      setLowestFee(stagedLowestFee);
      onFeeRiskLowerChange(stagedLowestFee);
    }
  };

  const handleOnBlurHighestFee = () => {
    if (stagedHighestFee !== null && highestFee !== stagedHighestFee) {
      setHighestFee(stagedHighestFee);
      onFeeRiskUpperChange(stagedHighestFee);
    }
  };

  const handleOnBlurFixedFee = () => {
    if (stagedFixedFee !== null) {
      if (fixedFee !== stagedFixedFee) {
        setFixedFee(stagedFixedFee);
        onFixedFeeAlternativeChange(stagedFixedFee);
      }
    }
  };

  const handleOnBlurCappedFee = () => {
    if (stagedCappedFee !== null) {
      if (cappedFee !== stagedCappedFee) {
        if (stagedCappedFee === 0) {
          form.setFieldsValue({ cappedFee: null });
          setStagedCappedFee(null);
          setCappedFee(null);
          onCappedFeeAlternativeChange(null);
        } else {
          setCappedFee(stagedCappedFee);
          onCappedFeeAlternativeChange(stagedCappedFee);
        }
      }
    }
  };

  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue({ fixedFee: fixedFee });
    validateAllFields();
    form.validateFields();
  }, [fixedFee]);
  useEffect(() => {
    form.setFieldsValue({ lowestAnticipatedFee: lowestAnticipatedFee });
    validateAllFields();
    form.validateFields();
  }, [lowestAnticipatedFee]);
  useEffect(() => {
    form.setFieldsValue({ highestAnticipatedFee: highestAnticipatedFee });
    validateAllFields();
    form.validateFields();
  }, [highestAnticipatedFee]);

  useEffect(() => {
    setFixedFee(fixedFeeAlternative);
  }, [fixedFeeAlternative]);

  useEffect(() => {
    form.validateFields();
    validateAllFields();
  }, []);

  useEffect(() => {
    validateAllFields();
  }, [lowestAnticipatedFee, highestAnticipatedFee, lowestFee, highestFee, fixedFee, cappedFee]);

  useEffect(() => {
    if (fixedFee !== null && highestAnticipatedFee !== null) {
      if (fixedFee < highestAnticipatedFee) {
        setShowFixedFeeWarning(true);
      } else {
        setShowFixedFeeWarning(false);
      }
    }
  }, [fixedFee, lowestAnticipatedFee]);

  useEffect(() => {
    if (cappedFee !== null && fixedFee !== null) {
      if (cappedFee < fixedFee) {
        setShowCappedFeeWarning(true);
      } else {
        setShowCappedFeeWarning(false);
      }
    }
  }, [cappedFee, fixedFee]);

  return (
    <div className={containerClassName}>
      <div className={headerClassName}>
        <h1>Fee Risk Profile Diagram</h1>
      </div>
      <div>
        <Form
          form={form}
          className={formContainerClassName}
          initialValues={{
            lowestAnticipatedFee: lowestAnticipatedFee,
            higestAnticipatedFee: highestAnticipatedFee,
            lowestFee: lowestFee,
            highestFee: highestFee,
            fixedFee: fixedFee,
            cappedFee: cappedFee,
            showCappedFee: showCappedFee,
          }}
          onValuesChange={() => {
            form.validateFields();
          }}
        >
          <Row gutter={16}>
            <Col span={12} key={0}>
              <Form.Item
                className={formItemClassName}
                label={InfoTooltip({
                  text: "Not editable on this screen. This has been inserted by VPD based on the calculations you have previously made under the 'Scope' tab.",
                  children: "Lowest anticipated fee",
                })}
                name="lowestAnticipatedFee"
              >
                <PriceInput
                  currencySymbol={currencySymbol}
                  className={amountInputClassname}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col span={12} key={1}>
              <Form.Item
                className={formItemClassName}
                label={InfoTooltip({
                  text: "Not editable on this screen. This has been inserted by VPD based on the calculations you have previously made under the 'Scope' tab.",
                  children: "Highest anticipated fee",
                })}
                name="highestAnticipatedFee"
              >
                <PriceInput
                  currencySymbol={currencySymbol}
                  className={amountInputClassname}
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12} key={0}>
              <Form.Item
                className={formItemClassName}
                label={InfoTooltip({
                  text: "The lowest and highest fees are intended to represent the second possible range based on a best and worst case scenario. You insert these figures here based on your judgment.",
                  children: "Lowest fee",
                })}
                name="lowestFee"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const validation = validateLowestFee({
                        lowestFee: value,
                        highestFee: getFieldValue("highestFee"),
                        lowestAnticipatedFee: getFieldValue("lowestAnticipatedFee"),
                        highestAnticipatedFee: getFieldValue("highestAnticipatedFee"),
                        fixedFee: getFieldValue("fixedFee"),
                        cappedFee: getFieldValue("cappedFee"),
                      });
                      if (validation) {
                        return Promise.reject(new Error(validation));
                      }
                    },
                  }),
                ]}
              >
                <PriceInput
                  currencySymbol={currencySymbol}
                  className={amountInputClassname}
                  onChange={(value) => setStagedLowestFee(value)}
                  onBlur={handleOnBlurLowestFee}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleOnBlurLowestFee();
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12} key={1}>
              <Form.Item
                className={formItemClassName}
                label={InfoTooltip({
                  text: "The lowest and highest fees are intended to represent the second possible range based on a best and worst case scenario. You insert these figures here based on your judgment.",
                  children: "Highest fee",
                })}
                name="highestFee"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const validation = validateHighestFee({
                        highestFee: value,
                        lowestFee: getFieldValue("lowestFee"),
                        lowestAnticipatedFee: getFieldValue("lowestAnticipatedFee"),
                        highestAnticipatedFee: getFieldValue("highestAnticipatedFee"),
                        fixedFee: getFieldValue("fixedFee"),
                        cappedFee: getFieldValue("cappedFee"),
                      });
                      if (validation) {
                        return Promise.reject(new Error(validation));
                      }
                    },
                  }),
                ]}
              >
                <PriceInput
                  currencySymbol={currencySymbol}
                  className={amountInputClassname}
                  onChange={(value) => setStagedHighestFee(value)}
                  onBlur={handleOnBlurHighestFee}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleOnBlurHighestFee();
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12} key={0}>
              <Form.Item
                className={formItemClassName}
                label={InfoTooltip({
                  text: "Conceptually, a fixed fee endeavours to share the risk and uncertainty between the client and the firm. The risk to the firm is that it must undertake work which isn't chargeable if it exceeds the fixed fee. The risk to the client is that the billable time comes to less than the fixed fee, but they are still obliged to pay the fixed fee. There is therefore a presumption that the fixed fee should be somewhere in the Fee Risk Zone. You insert the fixed fee here based on your judgment.",
                  children: "Fixed fee",
                })}
                name="fixedFee"
                rules={[
                  { required: true, message: "Fixed fee is required" },
                  { type: "number", min: 1, message: `Fee should be more than ${currencySymbol}0` },
                ]}
              >
                <PriceInput
                  currencySymbol={currencySymbol}
                  className={amountInputClassname}
                  onChange={(value) => setStagedFixedFee(value)}
                  onBlur={handleOnBlurFixedFee}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleOnBlurFixedFee();
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12} key={1}>
              <Form.Item
                className={formItemClassName}
                label={InfoTooltip({
                  text: "For reasons we all understand, capped fees should be avoided, since the firm will be paid the lesser of its time or the cap agreed to. However, if you have to option but to include a capped fee in your proposal, keep in mind that the risk to the firm is much higher and that risk should be mitigated by ensuring that the capped fee is place well into the Fee Risk Zone.",
                  children: "Capped fee",
                })}
                name="cappedFee"
              >
                <PriceInput
                  currencySymbol={currencySymbol}
                  className={amountInputClassname}
                  onChange={(value) => setStagedCappedFee(value)}
                  onBlur={handleOnBlurCappedFee}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleOnBlurCappedFee();
                    }
                  }}
                ></PriceInput>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12} key={0}></Col>
            <Col span={12} key={1}>
              <Form.Item
                className={formItemClassName}
                label="Include capped fee in diagram"
                name="showCappedFee"
                valuePropName="checked"
              >
                <Checkbox onChange={(e) => onShowCappedFeeChange(e.target.checked)} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
      {showFixedFeeWarning ? (
        <div className={alertClassName}>
          <Alert
            message="Warning"
            description={
              <div>
                You are attempting to set a fixed fee that is below the highest anticipated figure.
                As you can see, this will expose the firm to an unacceptable level of risk that the
                billable time will exceed the fixed fee.
              </div>
            }
            type="warning"
          />
        </div>
      ) : null}
      {cappedFee && feeRiskValid && showCappedFeeWarning ? (
        <div className={alertClassName}>
          <Alert
            message="Warning"
            description={
              <div>
                <p>
                  If you are offering the client the choice of either a capped fee or a fixed fee,
                  the capped fee should always be higher than the fixed fee. This relates to the
                  relative advantages, disadvantages, and risk profile of a capped fee versus a
                  fixed fee. With a capped fee, you will only ever be paid the lesser of the cap or
                  your accrued billable time.
                </p>
                <p>
                  In contrast, if you agree a fixed fee, that is the amount you will charge whether
                  the time is more than the fixed fee or less than the fixed fee. If your time is
                  less than the fixed fee, that represents a bonus. There is therefore potential
                  upside under a fixed fee, but there never can be with a capped fee, meaning that
                  you are entitled to eliminate as much of the risk under the capped fee as possible
                  by setting it higher than the fixed fee.
                </p>
              </div>
            }
            type="warning"
          />
        </div>
      ) : null}
      <FeeRiskProfileDiagram
        totalWidth={800}
        currencySymbol={currencySymbol}
        lowestAnticipatedFee={lowestAnticipatedFee ?? undefined}
        highestAnticipatedFee={highestAnticipatedFee ?? undefined}
        lowestFee={lowestFee ?? undefined}
        highestFee={highestFee ?? undefined}
        fixedFee={feeRiskValid ? fixedFee ?? undefined : undefined}
        cappedFee={showCappedFee && feeRiskValid ? cappedFee ?? undefined : undefined}
      />
    </div>
  );
};
